import React, { useEffect, useId, useRef } from 'react';

import { darkenHex, getRoleColorHex } from './constants';

const DRIFT_DUR = '3s';

/** Return a random percentage between 15% and 85%. */
const randPct = () => `${15 + Math.floor(Math.random() * 70)}%`;

/**
 * A circle with a subtle radial gradient based on a user's role color.
 * Base color with a slightly darker center at a randomized focal point.
 */
export const RoleColorDot = ({
  role,
  size = 14,
  hoverAnimate = false,
}: {
  role: string;
  size?: number;
  hoverAnimate?: boolean;
}) => {
  const id = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const baseColor = getRoleColorHex(role);
  const darkColor = darkenHex(baseColor, 0.85);

  // Deterministic "random" focal point from role string
  const hash = role
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const cx = 30 + (hash % 40); // 30–70%
  const cy = 30 + ((hash * 7) % 40); // 30–70%

  useEffect(() => {
    if (!hoverAnimate || !svgRef.current) return undefined;
    const svg = svgRef.current;
    const parent = svg.closest('.group');
    if (!parent) return undefined;

    const handleEnter = () => {
      svg.querySelectorAll('animate').forEach((anim) => {
        const start = anim.getAttribute('data-start') ?? '50%';
        const mid = randPct();
        anim.setAttribute('values', `${start};${mid};${start}`);
        (anim as SVGAnimateElement).beginElement();
      });
    };

    parent.addEventListener('mouseenter', handleEnter);
    return () => {
      parent.removeEventListener('mouseenter', handleEnter);
    };
  }, [hoverAnimate]);

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
    >
      <defs>
        <radialGradient id={`${id}-rg`} cx={`${cx}%`} cy={`${cy}%`} r="60%">
          <stop offset="0%" stopColor={darkColor} />
          <stop offset="100%" stopColor={baseColor} />
          {hoverAnimate && (
            <>
              <animate
                attributeName="cx"
                data-start={`${cx}%`}
                values={`${cx}%;${cx}%;${cx}%`}
                dur={DRIFT_DUR}
                begin="indefinite"
                fill="freeze"
              />
              <animate
                attributeName="cy"
                data-start={`${cy}%`}
                values={`${cy}%;${cy}%;${cy}%`}
                dur={DRIFT_DUR}
                begin="indefinite"
                fill="freeze"
              />
            </>
          )}
        </radialGradient>
      </defs>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={size / 2}
        fill={`url(#${id}-rg)`}
      />
    </svg>
  );
};
